import { GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import { enqueueSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { BENEFITS_URL } from 'src/config-global';
import { useBoolean } from 'src/hooks/use-boolean';
import { AxiosHttpClient } from 'src/http';
import { CouponsRepository } from 'src/repositories/benefits/coupons.repository';
import { useMyDataStore } from 'src/store/me/me.store';
import { getLpCouponsColumns } from './components/lp-coupons-table-columns/lp-coupons-table-columns';

export default function useLPCouponsList() {
  const [tabValue, setTabValue] = useState(0);
  const [benefits, setBenefits] = useState<any>([]);
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'name', sort: 'asc' }]);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    pageSize: 10,
    page: 0,
  });
  const [search, setSearch] = useState('');
  const [appliedFilters, setAppliedFilters] = useState<any>({});
  const isLoading = useBoolean(true);

  const navigate = useNavigate();

  const roles = useMyDataStore((state) => state.me?.roles);

  const api = new AxiosHttpClient(BENEFITS_URL);
  const repository = new CouponsRepository(api);

  const headers = getLpCouponsColumns(roles);

  const isUserLpPromoter = roles?.includes('lp_promoter');
  const lpPromoterData = useMyDataStore((state) => state.me?.lpEventsData);

  const initialVisibleColumns = roles?.some((role) => ['cn_admin', 'lp_promoter'].includes(role))
    ? {
        name: true,
        event: true,
        creatorName: true,
        totalQuantity: true,
        availableQuantity: false,
        createdAt: true,
        startDate: false,
        endDate: false,
      }
    : {
        name: true,
        event: false,
        creatorName: false,
        totalQuantity: true,
        availableQuantity: true,
        createdAt: true,
        startDate: true,
        endDate: true,
      };

  const onChangeTab = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setSortModel([]);
    setPaginationModel({ pageSize: 10, page: 0 });
  };

  const getData = async () => {
    const formatedFilters = {
      sort: JSON.stringify({ sortBy: sortModel[0]?.field, direction: sortModel[0]?.sort }),
      search,
      page: paginationModel.page,
      pageSize: paginationModel.pageSize,
      BUId: isUserLpPromoter ? lpPromoterData?.businessUnitId : appliedFilters?.bu,
      eventId: isUserLpPromoter ? lpPromoterData?.eventId : appliedFilters?.event,
    };

    isLoading.onTrue();
    try {
      if (tabValue === 0) {
        const response = await repository.getCouponsByApprovalStatus({
          approvalStatus: 'pending',
          ...formatedFilters,
        });
        if (response.isLeft()) {
          throw new Error(response.value.message);
        } else {
          setBenefits({
            ...response.value,
            data: response.value.data.map((b: any) => ({
              ...b,
              creatorName: b.lpEventsData.creatorName,
              eventName: b.lpEventsData.eventName,
              startDate: new Date(b.startDate).toLocaleDateString('pt-BR'),
              endDate: new Date(b.endDate).toLocaleDateString('pt-BR'),
              createdAt: new Date(b.createdAt).toLocaleDateString('pt-BR'),
            })),
          });
        }
      } else if (tabValue === 1) {
        const response = await repository.getCouponsByApprovalStatus({
          approvalStatus: 'approved',
          ...formatedFilters,
        });
        if (response.isLeft()) {
          throw new Error(response.value.message);
        } else {
          setBenefits({
            ...response.value,
            data: response.value.data.map((b: any) => ({
              ...b,
              creatorName: b.lpEventsData.creatorName,
              eventName: b.lpEventsData.eventName,
              startDate: new Date(b.startDate).toLocaleDateString('pt-BR'),
              endDate: new Date(b.endDate).toLocaleDateString('pt-BR'),
              createdAt: new Date(b.createdAt).toLocaleDateString('pt-BR'),
            })),
          });
        }
      } else {
        const response = await repository.getCouponsByApprovalStatus({
          approvalStatus: 'declined',
          ...formatedFilters,
        });
        if (response.isLeft()) {
          throw new Error(response.value.message);
        } else {
          setBenefits({
            ...response.value,
            data: response.value.data.map((b: any) => ({
              ...b,
              creatorName: b.lpEventsData.creatorName,
              eventName: b.lpEventsData.eventName,
              startDate: new Date(b.startDate).toLocaleDateString('pt-BR'),
              endDate: new Date(b.endDate).toLocaleDateString('pt-BR'),
              createdAt: new Date(b.createdAt).toLocaleDateString('pt-BR'),
            })),
          });
        }
      }
    } catch (error) {
      enqueueSnackbar({
        message: error.message,
        variant: 'error',
      });
    } finally {
      isLoading.onFalse();
    }
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      getData();
    }, 350);

    return () => {
      clearTimeout(timeout);
    };
  }, [tabValue, sortModel, paginationModel, search, appliedFilters]);

  return {
    tabValue,
    benefits,
    sortModel,
    paginationModel,
    search,
    headers,
    isLoading,
    appliedFilters,
    initialVisibleColumns,
    roles,
    setAppliedFilters,
    setSearch,
    setPaginationModel,
    setSortModel,
    navigate,
    onChangeTab,
  };
}
